import {
  Box,
  Button,
  ButtonProps,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  StackDivider,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DimensionUnit } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { formatAwbLabel, formatParsable, modelUtils } from '@pelicargo/utils';
import { useMemo, useState } from 'react';

import { trpc, TrpcOutputs } from '../config/trpc';
import { useDownloadDocument } from '../hooks/documents/useDownloadDocument';
import { DocumentChip } from './Documents/DocumentChip';
import { LabeledValue } from './LabeledValue';

// The two shipment-form payloads this read-only view is fed: the relation on the
// final quote and the getShipmentFormsByRequestId query PaperworkCard prefers.
export type ViewableShipmentForm =
  | TrpcOutputs['getShipmentFormsByRequestId'][number]
  | TrpcOutputs['getFinalQuote']['quote']['shipment_forms'][number];

type Props = {
  shipmentForms?: ViewableShipmentForm[];
  dimensionUnit: DimensionUnit;
  buttonProps?: ButtonProps;
};

/**
 * "View Instructions" button plus the read-only AWB Instructions dialog, mirroring the editable modal's layout: the
 * MAWB column on the left and, for consolidated shipments, the HAWBs tabbed on the right. Renders nothing until a MAWB
 * exists.
 */
export const AwbInstructionsViewModal = ({ shipmentForms, dimensionUnit, buttonProps }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast({ position: 'bottom', duration: 4000 });
  const { download, downloadingId } = useDownloadDocument();
  const { mutateAsync: deleteDocument } = trpc.deleteDocument.useMutation();
  const [deletingDocumentId, setDeletingDocumentId] = useState<number | null>(null);
  const [removedDocumentIds, setRemovedDocumentIds] = useState<number[]>([]);

  const orderedForms = useMemo(() => modelUtils.shipmentForms.orderMawbFirst(shipmentForms), [shipmentForms]);

  if (!orderedForms.length) return null;

  const [mawb, ...hawbs] = orderedForms;
  const isDirect = !hawbs.length;
  const minColumnWidth = '150px';

  const handleDeleteDocument = async (documentId: number) => {
    setDeletingDocumentId(documentId);
    try {
      await deleteDocument({ id: documentId });
      setRemovedDocumentIds((prev) => [...prev, documentId]);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const renderDocumentChip = (form: ViewableShipmentForm) => {
    const document = form.documents?.[0];
    if (!document || removedDocumentIds.includes(document.id)) return null;

    return (
      <Box borderWidth="1px" borderRadius="lg" px="3" py="2.5" mb="2">
        <DocumentChip
          document={document}
          fallbackLabel="AWB document"
          onNameClick={() => download(document.id)}
          isNameLoading={downloadingId === document.id}
          onDelete={() => handleDeleteDocument(document.id)}
          isDeleting={deletingDocumentId === document.id}
        />
      </Box>
    );
  };

  // The goods fields (shipper/consignee, nature, HS, cargo) live on the house bills, except for a direct shipment,
  // where the MAWB carries them itself — the same split the editable modal makes. A consolidated MAWB usually only
  // holds the document, so its empty fields are hidden rather than dashed.
  const renderFormFields = (form: ViewableShipmentForm, { isMawb }: { isMawb: boolean }) => {
    const carriesGoods = !isMawb || isDirect;
    const emptyFallback = carriesGoods ? '-' : undefined;

    return (
      <>
        <LabeledValue
          label="Shipper Details"
          value={formatParsable('Contact', form.shipper_contact)}
          defaultValue={emptyFallback}
          hideIfEmpty={!carriesGoods}
          minColumnWidth={minColumnWidth}
        />
        <LabeledValue
          label="Consignee Details"
          value={formatParsable('Contact', form.consignee_contact)}
          defaultValue={emptyFallback}
          hideIfEmpty={!carriesGoods}
          minColumnWidth={minColumnWidth}
        />
        <LabeledValue
          label="Notify Details"
          value={formatParsable('Contact', form.notify_contact)}
          hideIfEmpty
          minColumnWidth={minColumnWidth}
        />
        <LabeledValue
          label="Handling Instructions"
          value={form.handling_instructions}
          hideIfEmpty
          minColumnWidth={minColumnWidth}
        />
        <Show if={carriesGoods}>
          <LabeledValue
            label="Nature of Goods"
            value={form.nature_of_goods}
            defaultValue="-"
            minColumnWidth={minColumnWidth}
          />
          <LabeledValue label="HS Code(s)" value={form.hs_code} defaultValue="-" minColumnWidth={minColumnWidth} />
        </Show>
        <Show if={isMawb}>
          <LabeledValue label="ITN Number" value={form.itn_number} hideIfEmpty minColumnWidth={minColumnWidth} />
        </Show>
        <Show if={!isMawb}>
          <LabeledValue
            label="Cargo"
            value={modelUtils.cargos.summaryLines(form.cargo, dimensionUnit)}
            hideIfEmpty
            minColumnWidth={minColumnWidth}
          />
        </Show>
      </>
    );
  };

  return (
    <>
      <Button size="xs" variant="outline" onClick={onOpen} {...buttonProps}>
        View Instructions
      </Button>
      <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW={{ md: '4xl' }} minH={{ md: '36rem' }}>
          <ModalHeader pb="2" fontSize="3xl">
            AWB Instructions
            <Text fontSize="sm" fontWeight="normal" color="text.subtle" mt="1">
              Shipper, consignee and goods details provided for this shipment&apos;s air waybills.
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="6">
            <Stack direction={{ base: 'column', md: 'row' }} spacing="6" align="stretch" divider={<StackDivider />}>
              <Stack flex="1" spacing="3">
                <Heading variant="h6">MAWB</Heading>
                {renderDocumentChip(mawb)}
                {renderFormFields(mawb, { isMawb: true })}
              </Stack>
              <Show if={!isDirect}>
                <Stack flex="1" spacing="3">
                  <Tabs variant="enclosed" colorScheme="primary" isLazy>
                    <TabList>
                      {hawbs.map((form, index) => (
                        <Tab key={form.id}>{formatAwbLabel(index + 1)}</Tab>
                      ))}
                    </TabList>
                    <TabPanels>
                      {hawbs.map((form) => (
                        <TabPanel key={form.id} px="0">
                          <Stack spacing="3">
                            {renderDocumentChip(form)}
                            {renderFormFields(form, { isMawb: false })}
                          </Stack>
                        </TabPanel>
                      ))}
                    </TabPanels>
                  </Tabs>
                </Stack>
              </Show>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
